import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { classMap } from 'lit/directives/class-map.js';
import 'Components/atoms/vm-icon-next/vm-icon-next';

import Styles from './vm-video-isi.scss';

/**
 * Renders the Important Safety Information (ISI) block on the video detail page.
 *
 * Two display modes are controlled by `displayIsiPreview`:
 * - `false` (default): inline card on desktop, hidden on mobile (the bottom sheet
 *   surfaces it there instead — see `vm-video-info-bottom-sheet`).
 * - `true` (forced floating, used for ALLOWED_ISI_CHANNELS): collapsible
 *   fixed-position bar pinned to the viewport bottom on both desktop and mobile.
 */
@customElement('vm-video-isi')
export class VmVideoIsi extends LitElement {
    static override styles = Styles;

    @property({ type: String, attribute: 'safety-information' }) safetyInformation = '';
    @property({ type: Boolean, attribute: 'display-isi-preview', reflect: true }) displayIsiPreview = false;

    @state() private _floatingExpanded = false;

    private toggleFloating = () => {
        this._floatingExpanded = !this._floatingExpanded;
    };

    private renderFloating() {
        return html`
            <div
                class=${classMap({
                    'vm-video-isi__floating': true,
                    'vm-video-isi__floating--expanded': this._floatingExpanded,
                })}
            >
                <button
                    type="button"
                    class="vm-video-isi__toggle"
                    aria-expanded=${Boolean(this._floatingExpanded)}
                    aria-label=${this._floatingExpanded ? 'Collapse safety information' : 'Expand safety information'}
                    @click=${this.toggleFloating}
                >
                    <vm-icon-next name="chevron-down" class="vm-video-isi__chevron-icon"></vm-icon-next>
                </button>
                <div class="vm-video-isi__floating-body">${unsafeHTML(this.safetyInformation)}</div>
            </div>
        `;
    }

    private renderInlineCard() {
        return html`<div class="vm-video-isi__card">${unsafeHTML(this.safetyInformation)}</div>`;
    }

    override render() {
        if (!this.safetyInformation) return nothing;
        return this.displayIsiPreview ? this.renderFloating() : this.renderInlineCard();
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-video-isi': VmVideoIsi;
    }
}
